import { DownOutlined } from "@ant-design/icons"
import { Dropdown, Tooltip } from "antd"
import { type BaseOptionType } from "antd/lib/select"
import React from "react"
import { useTranslation } from "react-i18next"

import { cn } from "../../../utils/cn.ts"
import { LeadPill } from "./LeadPill.tsx"

import {
  type LeadStatusesValues,
  leadStatusColorMap,
  leadStatusDescriptionKeys,
  leadStatusStyles
} from "../../Lead/constants/lead-statuses.ts"
import {
  isLifecycleStage,
  stageIndex
} from "../../Lead/constants/lifecycle-stages.ts"
import {
  archiveLeadAllowedStatuses,
  archiveLeadStatuses
} from "../constants/archive-lead-allowed-statuses.ts"

const ARCHIVE_KEY = "archive"

interface LeadsStatusCellProps {
  /** The English stage label. It keys the style map and the option lookup, so
   *  it stays untranslated; pass `statusLabel` to change what is rendered. */
  statusDisplay: string
  statusLabel?: string
  isProject: boolean
  statusMap: BaseOptionType
  statuses: BaseOptionType[]
  editable?: boolean
  /** A lead can carry a live lifecycle stage and still be closed lost. */
  isClosedLost?: boolean
  /** In the Archive — no stage is offered, because the transition service
   *  refuses one. Restore is the way back. */
  isArchived?: boolean
  /** Opt-in only — lets a caller (the Overview Lifecycle card) size the pill itself. */
  className?: string
  onSelect: (newStatus: number) => void | Promise<void>
  onArchive?: () => void
}

export function LeadsStatusCell({
  statusDisplay,
  statusLabel,
  statusMap,
  statuses,
  isProject,
  editable,
  isClosedLost,
  isArchived,
  className,
  onSelect,
  onArchive
}: LeadsStatusCellProps) {
  const { t } = useTranslation()
  const styles = leadStatusStyles?.[statusDisplay]
  const disabledSelect = !editable || isProject || isArchived

  // Controlled rather than the Tooltip's own hover/focus trigger: rc-menu
  // moves real DOM focus onto the <li> itself as arrow keys roving-tabindex
  // through the menu, never onto a nested child, so the trigger has to sit
  // on the item's own onFocus/onMouseEnter (UI/UX guidelines §5.16 — hover
  // desktop, tap/focus mobile, 300ms delay).
  const [hoveredKey, setHoveredKey] = React.useState<React.Key | null>(null)
  const hoverTimeoutRef = React.useRef<ReturnType<typeof setTimeout>>(
    undefined
  )

  React.useEffect(() => () => window.clearTimeout(hoverTimeoutRef.current), [])

  if (disabledSelect) {
    return (
      <LeadPill style={styles} className={className}>
        {statusLabel ?? statusDisplay}
      </LeadPill>
    )
  }

  const TOOLTIP_HOVER_DELAY_MS = 300

  const tooltipHandlers = (key: React.Key) => ({
    onMouseEnter: () => {
      window.clearTimeout(hoverTimeoutRef.current)
      hoverTimeoutRef.current = setTimeout(
        () => setHoveredKey(key),
        TOOLTIP_HOVER_DELAY_MS
      )
    },
    onMouseLeave: () => {
      window.clearTimeout(hoverTimeoutRef.current)
      setHoveredKey((current) => (current === key ? null : current))
    },
    onFocus: () => setHoveredKey(key),
    onBlur: () =>
      setHoveredKey((current) => (current === key ? null : current))
  })

  // `label` covers only the text: wrapping it (not the whole row) keeps the
  // item's own click/selection area, padding and width untouched.
  const withDescription = (
    key: React.Key,
    label: React.ReactNode,
    description?: string
  ) =>
    description ? (
      <Tooltip
        title={description}
        open={hoveredKey === key}
        placement="right"
        destroyOnHidden
      >
        {/* rc-menu drops its own onMouseEnter/onMouseLeave wiring for a
            disabled item — the current status — so the label repeats the
            hover handlers here as the fallback that row can still fire.
            `block w-full` stretches the hit area to the row's own width so
            leaving the text but not the row doesn't hide the tooltip early. */}
        <span
          className="block w-full"
          onMouseEnter={tooltipHandlers(key).onMouseEnter}
          onMouseLeave={tooltipHandlers(key).onMouseLeave}
        >
          {label}
        </span>
      </Tooltip>
    ) : (
      label
    )

  const currentStatus = statusMap?.[statusDisplay]
  const isArchivedStatus = archiveLeadStatuses?.includes(currentStatus)

  // A lead can sit on a retired status, so its tag still renders — but the
  // transition endpoint rejects any target outside the lifecycle outright, so
  // offering one would only ever produce a 400. Listed in lifecycle order,
  // which is not the numeric order the options arrive in: Booked, Exploration
  // and Contract were added after the enum.
  const allowedStatuses = (
    isArchivedStatus
      ? statuses?.filter((s) => archiveLeadAllowedStatuses?.includes(s?.value))
      : statuses
  )
    ?.filter((s) => isLifecycleStage(s?.value))
    ?.sort((a, b) => stageIndex(a?.value) - stageIndex(b?.value))

  const stageItems = allowedStatuses?.map(({ label, value }) => {
    const disabled = value === currentStatus
    const style = leadStatusColorMap?.[value as LeadStatusesValues]
    const descriptionKey =
      leadStatusDescriptionKeys[value as LeadStatusesValues]

    return {
      key: value,
      label: withDescription(
        value,
        label,
        descriptionKey && t(descriptionKey)
      ),
      className: "mb-1",
      disabled,
      style: disabled ? {} : style,
      ...tooltipHandlers(value)
    }
  })

  // The archive form owns the outcome choice. Keeping Lost and Unqualified out
  // of this stage list avoids presenting archive outcomes as lifecycle stages.
  const archiveItem =
    onArchive && !isArchivedStatus && !isClosedLost
      ? {
          key: ARCHIVE_KEY,
          label: withDescription(
            ARCHIVE_KEY,
            t("leadsTable.status.archiveGroup"),
            t("leadsTable.status.descriptions.archive")
          ),
          className: "mb-1",
          ...tooltipHandlers(ARCHIVE_KEY)
        }
      : null

  return (
    <Dropdown
      menu={{
        items: archiveItem
          ? [
              ...(stageItems ?? []),
              { type: "divider" as const },
              archiveItem
            ]
          : stageItems,
        onClick: ({ key, domEvent }) => {
          domEvent.stopPropagation()
          if (key === ARCHIVE_KEY) return onArchive?.()
          if (key !== statusDisplay) onSelect(+key)
        }
      }}
    >
      {/* A Tag is a span, so the trigger reaches the accessibility tree only if
          it says what it is and what it holds. */}
      <LeadPill
        role={"button"}
        tabIndex={0}
        aria-haspopup={"menu"}
        aria-label={t("leadsTable.status.change", { status: statusDisplay })}
        style={styles}
        className={cn("cursor-pointer", className)}
      >
        {statusDisplay}
        <DownOutlined className="text-xs" />
      </LeadPill>
    </Dropdown>
  )
}
